import { GalleryComponent } from '@/app/components/shared/gallery/gallery.component';
import { CommonModule } from '@angular/common';
import { Component, ViewChild, TemplateRef, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { environment } from '@/app/environments/environment';
import { Router } from '@angular/router';

// Services
import { ProjectsService } from '@/app/services/projects/projects.service';
import { ProjectUtilityService } from '@/app/services/utility/project-utility.service';

@Component({
  selector: 'app-brief-view-history',
  standalone: true,
  imports: [CommonModule, GalleryComponent, MatDialogModule, MatButtonModule],
  templateUrl: './brief-view-history.component.html',
  styleUrl: './brief-view-history.component.css',
})
export class BriefViewHistoryComponent implements OnInit {
  // FROM PARAMS
  revisionId!: number;

  /**
   * @ViewChild is a decorator in Angular that is used to get a reference to a DOM element,
   * directive, or template within the component's view.
   * This defines a class property named dialogTemplate, which will hold a reference to the template.
   */
  @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<any>;

  projectData: any;
  projectTypeData: any;
  projectCountryData: any;
  projectGradeData: any;
  locationAttachments: any[] = [];
  siteConditionAttachments: any[] = [];
  mapLink!: SafeResourceUrl;
  google_api_key = environment.apiKey;

  constructor(
    private ActivatedRoute: ActivatedRoute,
    private projectsService: ProjectsService,
    private projectUtilityService: ProjectUtilityService,
    private sanitizer: DomSanitizer,
    public dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    // get project revision if from param
    this.ActivatedRoute.queryParams.subscribe((params) => {
      this.revisionId = +params['revisionId']; // Use '+' to convert the string to a number
    });

    this.getProject();
  }

  getSanitizedHtml(description: string) {
    if (!description) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(description);
  }

  numberToWords(num: any) {
    if (num === 0) return 'zero';

    const belowTwenty = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      '',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];
    const thousands = ['', 'thousand', 'million', 'billion', 'trillion'];

    function helper(n) {
      if (n === 0) return '';
      else if (n < 20) return belowTwenty[n] + ' ';
      else if (n < 100) return tens[Math.floor(n / 10)] + ' ' + helper(n % 10);
      else
        return belowTwenty[Math.floor(n / 100)] + ' hundred ' + helper(n % 100);
    }

    let word = '';
    let index = 0;

    while (num > 0) {
      if (num % 1000 !== 0) {
        word = helper(num % 1000) + thousands[index] + ' ' + word;
      }
      num = Math.floor(num / 1000);
      index++;
    }

    return word.trim();
  }

  openDialog(): void {
    this.dialog.open(this.dialogTemplate);
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }
  handleClose() {
    this.router.navigate([`/project-revision`]);
  }
  // TODO:
  print() {
    const content = document.getElementById('projectBriefView');

    if (content) {
      html2canvas(content, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 190; // Width of the image in the PDF
        const pageHeight = pdf.internal.pageSize.height;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add pages if content exceeds one page
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save('project-brief.pdf');
      });
    }

    this.closeDialog();
  }

  // ################################### SERVICE CALLS START ###################################
  getProject(): void {
    this.projectsService.getDataViewMode(this.revisionId).subscribe({
      next: (response: any) => {
        this.projectData = JSON.parse(response?.data?.data);

        if (
          this.projectData?.projectData?.latitude &&
          this.projectData?.projectData?.longitude
        ) {
          const longitutde = this.projectData?.projectData?.longitude;
          const latitude = this.projectData?.projectData?.latitude;
          const url = `https://www.google.com/maps/embed/v1/place?key=${this.google_api_key}&q=${latitude},${longitutde}&zoom=16`;

          this.mapLink = this.sanitizer?.bypassSecurityTrustResourceUrl(url);
        }

        if (this.projectData?.projectLocationData) {
          this.locationAttachments = this.projectData?.projectLocationData;
        }

        if (this.projectData?.projectSiteConditionData) {
          this.siteConditionAttachments =
            this.projectData?.projectSiteConditionData;
        }

        // NOTE: we can store projecttypetree when creating history. can do it later
        if (this.projectData?.projectData?.project_type_id) {
          this.getProjectTypeTree();
        }
        if (this.projectData?.projectData?.country_id) {
          this.getProjectCountry(this.projectData?.projectData?.country_id);
        }
        if (this.projectData?.projectData?.grade_id) {
          this.getProjectGrade(this.projectData?.projectData?.grade_id);
        }
      },
      error: (error) => {
        console.error('Error fetching Details:', error);
      },
    });
  }

  getProjectTypeTree(): any {
    this.projectUtilityService
      .getProjectTypeTree(this.projectData?.projectData?.project_type_id)
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            this.projectTypeData =
              this.projectUtilityService.formattedProjectTypeTree(
                response?.data
              );
          }
        },
        error: (error) => {
          console.error('Error fetching Details:', error);
        },
      });
  }

  getProjectCountry(country_id): any {
    this.projectUtilityService.getProjectCountry(country_id).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.projectCountryData = response?.data;
        }
      },
      error: (error) => {
        console.error('Error fetching Details:', error);
      },
    });
  }

  getProjectGrade(grade_id): any {
    this.projectUtilityService.getProjectGrade(grade_id).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.projectGradeData = response?.data;
        }
      },
      error: (error) => {
        console.error('Error fetching Details:', error);
      },
    });
  }

  // ################################### SERVICE CALLS END ###################################
}
